import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeModalState } from '../../../modules/actions';
import {
  getCollectionId,
  getCommentCountForImage,
  getCommentCountsByImageId
} from '../../../modules/selectors';
import { IStoreState } from '../../../modules/types';
import { useNewCommentsDesign } from '../../../hooks/useNewCommentsDesign';

/**
 * Opens the comment history for one image. Shared by the tile badge, the
 * fullscreen toolbar and the virtualised grids.
 */
export const useOpenImageComments = () => {
  const dispatch = useDispatch();
  const collectionId = useSelector(getCollectionId);
  const isNewCommentsDesign = useNewCommentsDesign();

  const openImageComments = useCallback(
    (imageId: string) => {
      dispatch(
        changeModalState({
          key: 'imageCommentsModal',
          state: true,
          modalData: { commentsImageId: imageId, commentsCollectionId: collectionId }
        })
      );
    },
    [dispatch, collectionId]
  );

  return { isNewCommentsDesign, openImageComments };
};

/**
 * Count for a single image. Subscribes to the number rather than the whole map,
 * so one image gaining a comment cannot re-render every other tile.
 */
export const useImageCommentCount = (imageId?: string): number =>
  useSelector((state: IStoreState) => (imageId ? getCommentCountForImage(state, imageId) : 0));

/**
 * Whole imageId -> count map, for the virtualised grids that render many tiles
 * from one `itemData` object and would otherwise need one subscription per cell.
 * Returns undefined when the flag is off so the grids can skip the badge wiring.
 */
export const useImageCommentCounts = () => {
  const { isNewCommentsDesign, openImageComments } = useOpenImageComments();
  const countsByImageId = useSelector(getCommentCountsByImageId);

  return {
    isNewCommentsDesign,
    openImageComments: isNewCommentsDesign ? openImageComments : undefined,
    commentCounts: isNewCommentsDesign ? countsByImageId : undefined
  };
};
